import { Timestamp } from '@angular/fire/firestore';

/**
 * Utility Meter Model for Belgian Property Management
 * Covers electricity, gas, water, and other utility meters
 */

export type MeterType = 'electricity' | 'gas' | 'water' | 'heating' | 'other';

export type MeterSubType = 
  // Electricity subtypes
  | 'single_rate' | 'dual_rate' | 'solar_production' | 'heat_pump'
  // Gas subtypes  
  | 'natural_gas' | 'propane'
  // Water subtypes
  | 'cold_water' | 'hot_water' | 'heating_water'
  // Heating subtypes
  | 'district_heating' | 'oil_heating'
  // Other
  | 'other';

export type ContractHolder = 'owner' | 'tenant' | 'unknown';

export interface MeterReading {
  /** Reading value */
  value: number;
  /** Date of reading */
  date: Timestamp;
  /** Person who took the reading */
  readBy?: string;
  /** Reading type (manual, automatic, estimated) */
  type: 'manual' | 'automatic' | 'estimated';
  /** Optional notes about the reading */
  notes?: string;
  /** Photo of the meter showing this reading */
  photoUrl?: string;
}

export interface MeterDocument {
  /** Document ID */
  id: string;
  /** Document name/title */
  name: string;
  /** Document type */
  type: 'installation_certificate' | 'calibration_certificate' | 'contract' | 'invoice' | 'photo' | 'transfer_request' | 'other';
  /** Document URL */
  url: string;
  /** Upload date */
  uploadDate: Timestamp;
  /** File size in bytes */
  size?: number;
  /** MIME type */
  mimeType?: string;
  /** Full Firebase Storage path — kept so the blob can be deleted on removal */
  storagePath?: string;
  /** Optional description */
  description?: string;
}

export interface Meter {
  /** Unique meter ID */
  meterUID: string;
  
  /** Property this meter belongs to */
  propertyUID: string;
  
  /** Meter type (electricity, gas, water, etc.) */
  type: MeterType;
  
  /** Meter subtype for more specific categorization */
  subType?: MeterSubType;
  
  /** EAN code (European Article Number) - required for electricity and gas in Belgium */
  eanCode?: string;
  
  /** Physical meter number/ID as shown on the meter */
  meterNumber: string;
  
  /** Location of the meter within the property */
  location: string;
  
  /** Current contract holder (owner or tenant) */
  contractHolder: ContractHolder;
  
  /** Current meter reading */
  currentReading?: number;

  /** Date of current reading */
  currentReadingDate?: Timestamp;

  /** Historical readings */
  readings: MeterReading[];

  /** Meter specifications */
  specifications?: {
    /** Manufacturer */
    manufacturer?: string;
    /** Model */
    model?: string;
    /** Serial number */
    serialNumber?: string;
    /** Maximum capacity/flow */
    maxCapacity?: number;
    /** Unit of measurement */
    unit?: string;
    /** Installation type (wall, floor, cabinet, etc.) */
    installationType?: string;
  };

  /** Utility provider / energy contract information */
  provider?: {
    /** Provider name (fournisseur) */
    name?: string;
    /** Customer number */
    customerNumber?: string;
    /** Contract number */
    contractNumber?: string;
    /** Contact phone */
    phone?: string;
    /** Contact email */
    email?: string;
    /** Start date of the energy contract */
    contractStartDate?: Timestamp;
    /** End date of the energy contract */
    contractEndDate?: Timestamp;
  };

  /** Documents and photos */
  documents: MeterDocument[];

  /** Status of the meter */
  status: 'active' | 'inactive' | 'maintenance' | 'replaced';

  /** General notes */
  notes?: string;

  /** Audit fields */
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string;
  updatedBy?: string;
  
  /** Soft delete flag */
  deleted?: boolean;
}

/**
 * Provider / energy-contract payload as captured by the form layer.
 *
 * Mirrors `Meter['provider']` but the contract dates are plain `Date`s here —
 * the service converts them to Firestore `Timestamp`s on write (same pattern as
 * `currentReadingDate`).
 */
export interface MeterProviderInput {
  name?: string;
  customerNumber?: string;
  contractNumber?: string;
  phone?: string;
  email?: string;
  contractStartDate?: Date;
  contractEndDate?: Date;
}

/**
 * Convert a form-layer provider payload into the stored shape: contract dates
 * become Firestore `Timestamp`s and blank/`undefined` keys are dropped (the
 * client Firestore SDK rejects nested `undefined` — there is no
 * `ignoreUndefinedProperties`). Returns `undefined` when nothing meaningful was
 * provided. Shared by the real `MetersService` and the mock store so both
 * persist the provider identically.
 */
export function toStoredMeterProvider(provider?: MeterProviderInput): Meter['provider'] | undefined {
  if (!provider) {
    return undefined;
  }
  const out: NonNullable<Meter['provider']> = {};
  if (provider.name?.trim()) out.name = provider.name.trim();
  if (provider.customerNumber?.trim()) out.customerNumber = provider.customerNumber.trim();
  if (provider.contractNumber?.trim()) out.contractNumber = provider.contractNumber.trim();
  if (provider.phone?.trim()) out.phone = provider.phone.trim();
  if (provider.email?.trim()) out.email = provider.email.trim();
  if (provider.contractStartDate) out.contractStartDate = Timestamp.fromDate(provider.contractStartDate);
  if (provider.contractEndDate) out.contractEndDate = Timestamp.fromDate(provider.contractEndDate);
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Interface for creating a new meter (without generated fields)
 */
export interface CreateMeterRequest {
  propertyUID: string;
  type: MeterType;
  subType?: MeterSubType;
  eanCode?: string;
  meterNumber: string;
  location: string;
  contractHolder: ContractHolder;
  currentReading?: number;
  currentReadingDate?: Date;
  specifications?: Meter['specifications'];
  provider?: MeterProviderInput;
  notes?: string;
}

/**
 * Interface for updating meter information
 */
export interface UpdateMeterRequest {
  type?: MeterType;
  subType?: MeterSubType;
  eanCode?: string;
  meterNumber?: string;
  location?: string;
  contractHolder?: ContractHolder;
  specifications?: Meter['specifications'];
  provider?: MeterProviderInput;
  status?: Meter['status'];
  notes?: string;
}

/**
 * Interface for adding a new meter reading
 */
export interface AddMeterReadingRequest {
  value: number;
  date: Date;
  readBy?: string;
  type: 'manual' | 'automatic' | 'estimated';
  notes?: string;
  photoUrl?: string;
}

/**
 * Utility functions for meter management
 */
export class MeterUtils {
  /**
   * Get display name for meter type
   */
  static getMeterTypeDisplayName(type: MeterType): string {
    const typeNames: Record<MeterType, string> = {
      electricity: 'Electricity',
      gas: 'Gas',
      water: 'Water',
      heating: 'Heating',
      other: 'Other'
    };
    return typeNames[type] || type;
  }
  
  /**
   * Get display name for meter subtype
   */
  static getMeterSubTypeDisplayName(subType: MeterSubType): string {
    const subTypeNames: Record<MeterSubType, string> = {
      single_rate: 'Single Rate',
      dual_rate: 'Dual Rate (Day/Night)',
      solar_production: 'Solar Production',
      heat_pump: 'Heat Pump',
      natural_gas: 'Natural Gas',
      propane: 'Propane',
      cold_water: 'Cold Water',
      hot_water: 'Hot Water',
      heating_water: 'Heating Water',
      district_heating: 'District Heating',
      oil_heating: 'Oil Heating',
      other: 'Other'
    };
    return subTypeNames[subType] || subType;
  }
  
  /**
   * Get available subtypes for a meter type
   */
  static getSubTypesForType(type: MeterType): MeterSubType[] {
    const subTypeMap: Record<MeterType, MeterSubType[]> = {
      electricity: ['single_rate', 'dual_rate', 'solar_production', 'heat_pump'],
      gas: ['natural_gas', 'propane'],
      water: ['cold_water', 'hot_water', 'heating_water'],
      heating: ['district_heating', 'oil_heating'],
      other: ['other']
    };
    return subTypeMap[type] || ['other'];
  }
  
  /**
   * Validate EAN code format (13 digits for Belgium)
   */
  static validateEanCode(eanCode: string): boolean {
    return /^\d{13}$/.test(eanCode);
  }
  
  /**
   * Get contract holder display name
   */
  static getContractHolderDisplayName(holder: ContractHolder): string {
    const holderNames: Record<ContractHolder, string> = {
      owner: 'Property Owner',
      tenant: 'Tenant',
      unknown: 'Unknown'
    };
    return holderNames[holder] || holder;
  }
  
  /**
   * Calculate consumption between two readings
   */
  static calculateConsumption(currentReading: number, previousReading: number): number {
    return Math.max(0, currentReading - previousReading);
  }
  
  /**
   * Get meter status color for UI
   */
  static getStatusColor(status: Meter['status']): string {
    const colors: Record<Meter['status'], string> = {
      active: 'green',
      inactive: 'gray',
      maintenance: 'orange',
      replaced: 'red'
    };
    return colors[status] || 'gray';
  }
}